import * as React from 'react';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, styled, Typography, Hidden } from '@mui/material';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import FooterLogo from 'public/footerLogo.svg';
import useSiteContext from 'src/store/slices/site';
import theme from 'src/themes';

const FooterContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  width: '100%',
  backgroundColor: theme.palette.primary.dark,
  padding: theme.spacing(2),
  marginTop: 'auto',
}));

const FooterBox = styled(Box)(() => ({
  [theme.breakpoints.up('md')]: {
    margin: 'auto',
    maxWidth: theme.breakpoints.values.lg,
  },
}));

const FooterLogoContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  [theme.breakpoints.up('md')]: {
    justifyContent: 'center',
    flexDirection: 'row',
  },
  '& p': {
    color: theme.palette.common.white,
    fontSize: theme.typography.body2.fontSize,
  },
}));

const FooterLogoBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0.7, 1.3),
}));

const StyledLink = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  color: theme.palette.common.white,
  fontSize: theme.typography.body1.fontSize,
  fontFamily: theme.typography.fontFamily,
  lineHeight: theme.typography.h5.lineHeight,
  padding: theme.spacing(1, 2),
  [theme.breakpoints.up('md')]: {
    justifyContent: 'center',
  },
}));

const LinksContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}));

const year = new Date().getFullYear();
const copyrightText = 'ORCHA Health Ltd';

export const Footer = () => {
  const { t } = useTranslation();
  const { footer } = useSiteContext();

  const footerLinks =
    footer?.additionalNavigation.sort(function (a, b) {
      return a.order - b.order;
    }) ?? [];

  return (
    <FooterContainer component="footer" data-testid="footer-container">
      <FooterBox>
        <LinksContainer>
          <StyledLink>
            <Link href="/terms-of-use">{t('footer.terms')}</Link>
          </StyledLink>
          <StyledLink>
            <Link href="/privacy-policy">{t('footer.privacyPolicy')}</Link>
          </StyledLink>
          <StyledLink>
            <Link href="/cookie-policy">{t('footer.cookiePolicy')}</Link>
          </StyledLink>

          {footerLinks.map((item) =>
            item.name && item.urlSlug ? (
              <StyledLink key={item.name}>
                <Link href={item.urlSlug} key={item.name}>
                  {item.name}
                </Link>
              </StyledLink>
            ) : (
              item.name &&
              item.externalLink && (
                <StyledLink key={item.name}>
                  <a
                    href={item.externalLink}
                    key={item.name}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {item.name}
                  </a>
                  <OpenInNewIcon
                    sx={{
                      width: '14px',
                      height: '14px',
                      marginLeft: theme.spacing(0.5),
                    }}
                  />
                </StyledLink>
              )
            )
          )}
        </LinksContainer>

        <FooterLogoContainer>
          <FooterLogoBox>
            <Typography variant="body1">
              &#xA9;{year}&nbsp;
              {copyrightText}
            </Typography>
          </FooterLogoBox>
          <Hidden lgDown>
            <FooterLogoBox>
              <FooterLogo title={t('footer.logoTitle')} />
            </FooterLogoBox>
          </Hidden>
        </FooterLogoContainer>
      </FooterBox>
    </FooterContainer>
  );
};

export default Footer;
